import { useTranslation } from 'react-i18next';
import type { QualityLevel } from './useHlsQuality';

interface Props {
  levels: QualityLevel[];
  /** Active level index; -1 = Auto. */
  current: number;
  /** Select a rendition (or -1 for Auto). Drives playback; the download uses
   *  the same selection (Auto → highest, resolved in useStreamDownload). */
  onSelect: (index: number) => void;
}

function levelLabel(level: QualityLevel): string {
  if (typeof level.height === 'number') return `${level.height}p`;
  return `${Math.round(level.bitrate / 1000)} kbps`;
}

/**
 * Settings-tab body listing Auto + each available rendition. The active one is
 * marked via aria-pressed; clicking sets the hls.js level. Renditions are shown
 * highest-first so the best quality is at the top.
 */
export function QualityPanel({ levels, current, onSelect }: Props): JSX.Element {
  const { t } = useTranslation();
  const sorted = [...levels].sort((a, b) => b.bitrate - a.bitrate);
  return (
    <div className="quality-panel" data-testid="quality-panel">
      <button
        type="button"
        className="quality-panel__item"
        aria-pressed={current === -1}
        onClick={(): void => onSelect(-1)}
      >
        {t('player.quality.auto')}
      </button>
      {sorted.map((level) => (
        <button
          key={level.index}
          type="button"
          className="quality-panel__item"
          aria-pressed={current === level.index}
          onClick={(): void => onSelect(level.index)}
        >
          {levelLabel(level)}
        </button>
      ))}
    </div>
  );
}
